import React, { useState, useEffect } from "react";
import api from "../../lib/api";
import { Plus, Edit2, Trash2, X, Save, Monitor } from "lucide-react";
import { useConfirm } from "../../contexts/ConfirmContext";

const HW_TYPES = ["PC", "Laptop", "Server", "Drucker", "NAS", "Switch", "Router", "Firewall", "Monitor", "Tastatur", "Maus", "Sonstiges"];

function HwRow({ item, customerId, onDelete, onUpdate }) {
  const [showEdit, setShowEdit] = useState(false);
  const [form, setForm] = useState({ ...item });

  const save = async (e) => {
    e.preventDefault();
    const r = await api.put(`/customers/${customerId}/hardware/${item._id}`, form);
    onUpdate(r.data);
    setShowEdit(false);
  };

  return (
    <>
      <tr data-testid={`hardware-row-${item._id}`}>
        <td>
          <div className="flex items-center gap-2">
            <Monitor size={14} className="text-muted-foreground" />
            <span className="font-medium">{item.name}</span>
          </div>
        </td>
        <td className="text-xs text-muted-foreground">{item.type || "—"}</td>
        <td className="text-xs">{item.manufacturer || "—"}</td>
        <td className="text-xs">{item.model || "—"}</td>
        <td className="font-mono text-xs">{item.serial_number || "—"}</td>
        <td className="text-xs">{item.os ? `${item.os} ${item.os_version || ""}`.trim() : "—"}</td>
        <td className="text-xs">{item.warranty_until || "—"}</td>
        <td>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowEdit(!showEdit)} className="text-muted-foreground hover:text-primary transition-colors"><Edit2 size={13} /></button>
            <button onClick={() => onDelete(item._id)} className="text-muted-foreground hover:text-destructive transition-colors"><Trash2 size={13} /></button>
          </div>
        </td>
      </tr>
      {showEdit && (
        <tr>
          <td colSpan={8} className="bg-muted/30 p-4">
            <form onSubmit={save} className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { name: "name", label: "Name *", required: true },
                { name: "manufacturer", label: "Hersteller" },
                { name: "model", label: "Modell" },
                { name: "serial_number", label: "Seriennummer" },
                { name: "purchase_date", label: "Kaufdatum" },
                { name: "warranty_until", label: "Garantie bis" },
                { name: "os", label: "Betriebssystem" },
                { name: "os_version", label: "OS-Version" },
                { name: "location", label: "Standort" },
                { name: "notes", label: "Notizen" },
              ].map(f => (
                <div key={f.name}>
                  <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1">{f.label}</label>
                  <input value={form[f.name] || ""} onChange={e => setForm({ ...form, [f.name]: e.target.value })} required={f.required}
                    className="w-full px-2 py-1.5 text-sm bg-background border border-input focus:border-primary focus:outline-none" />
                </div>
              ))}
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1">Typ</label>
                <select value={form.type || ""} onChange={e => setForm({ ...form, type: e.target.value })}
                  className="w-full px-2 py-1.5 text-sm bg-background border border-input focus:border-primary focus:outline-none">
                  {HW_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="col-span-full flex gap-2">
                <button type="submit" className="px-3 py-1.5 bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 flex items-center gap-1"><Save size={12} /> Speichern</button>
                <button type="button" onClick={() => setShowEdit(false)} className="px-3 py-1.5 bg-secondary text-xs font-semibold flex items-center gap-1"><X size={12} /> Abbrechen</button>
              </div>
            </form>
          </td>
        </tr>
      )}
    </>
  );
}

export default function HardwareTab({ customerId }) {
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", type: "PC", manufacturer: "", model: "", serial_number: "", purchase_date: "", warranty_until: "", os: "", os_version: "", location: "", notes: "" });

  useEffect(() => {
    api.get(`/customers/${customerId}/hardware`).then(r => setItems(r.data));
  }, [customerId]);

  const create = async (e) => {
    e.preventDefault();
    const r = await api.post(`/customers/${customerId}/hardware`, form);
    setItems([...items, r.data]);
    setForm({ name: "", type: "PC", manufacturer: "", model: "", serial_number: "", purchase_date: "", warranty_until: "", os: "", os_version: "", location: "", notes: "" });
    setShowForm(false);
  };

  const remove = async (id) => {
    if (!(await confirm({ title: "Hardware löschen", message: "Dieser Hardware-Eintrag wird dauerhaft entfernt.", confirmLabel: "Löschen" }))) return;
    await api.delete(`/customers/${customerId}/hardware/${id}`);
    setItems(items.filter(i => i._id !== id));
  };

  return (
    <div className="max-w-6xl animate-in">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Hardware-Inventar</h2>
        <button data-testid="add-hardware-button" onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90">
          <Plus size={13} /> Neues Gerät
        </button>
      </div>

      {showForm && (
        <form onSubmit={create} className="bg-card border border-border p-4 mb-4 animate-in">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { name: "name", label: "Name *", required: true },
              { name: "manufacturer", label: "Hersteller" },
              { name: "model", label: "Modell" },
              { name: "serial_number", label: "Seriennummer" },
              { name: "purchase_date", label: "Kaufdatum" },
              { name: "warranty_until", label: "Garantie bis" },
              { name: "os", label: "Betriebssystem" },
              { name: "os_version", label: "OS-Version" },
              { name: "location", label: "Standort" },
              { name: "notes", label: "Notizen" },
            ].map(f => (
              <div key={f.name}>
                <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1">{f.label}</label>
                <input data-testid={`hardware-form-${f.name}`} value={form[f.name]} onChange={e => setForm({ ...form, [f.name]: e.target.value })}
                  required={f.required} className="w-full px-3 py-2 text-sm bg-background border border-input focus:border-primary focus:outline-none" />
              </div>
            ))}
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1">Typ</label>
              <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}
                className="w-full px-3 py-2 text-sm bg-background border border-input focus:border-primary focus:outline-none">
                {HW_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button data-testid="save-hardware-button" type="submit" className="px-3 py-1.5 bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90">Speichern</button>
            <button type="button" onClick={() => setShowForm(false)} className="px-3 py-1.5 bg-secondary text-xs font-semibold">Abbrechen</button>
          </div>
        </form>
      )}

      <div className="bg-card border border-border overflow-x-auto" data-testid="hardware-table">
        {items.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Keine Hardware erfasst</div>
        ) : (
          <table className="w-full data-table">
            <thead><tr><th>Name</th><th>Typ</th><th>Hersteller</th><th>Modell</th><th>Seriennr.</th><th>OS</th><th>Garantie</th><th></th></tr></thead>
            <tbody>
              {items.map(i => <HwRow key={i._id} item={i} customerId={customerId} onDelete={remove} onUpdate={u => setItems(items.map(x => x._id === u._id ? u : x))} />)}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
